import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import BookDemo from "./BookDemo";
import BrandIntro, { hasSeenIntro } from "./BrandIntro";
import { SiteNav, SiteFooter, NAV_SECTIONS } from "./SiteChrome";
import ScrollChapters from "./ScrollChapters";
import PrivacySection from "./PrivacySection";
import { onMarketingConsent } from "./consent";
import { trackViewContent } from "./tracking";
import { mobileStyle } from "./MobileAppStyles";

import heroBackground from "./assets/MobileApp/herobackground.png";
import podiumLeft from "./assets/MobileApp/leftpodium.png";
import podiumRight from "./assets/MobileApp/rightpodium.png";
import screenHome from "./assets/MobileApp/Home-1.png";
import screenJournal from "./assets/MobileApp/Journal.png";
import screenChatbot from "./assets/MobileApp/Chatbot.png";
import screenInsights from "./assets/MobileApp/InsightsPage-1.png";
import screenWellbeing from "./assets/MobileApp/WellBeing.png";
import screenGoals from "./assets/MobileApp/Goals.png";
import screenCounsellor from "./assets/MobileApp/Counsellor-1.png";
import screenPulse from "./assets/MobileApp/EmotionPulse.png";

/* The app is not published yet, so there is nothing to link to. The badges
   below render as static "coming soon" markers rather than links to "#", which
   would promise a destination that does not exist. Swap these in  and turn the
   badges back into anchors  once the listings are live. */
const STORE_LINKS = { ios: null, android: null };

/* The page's one call to action: every primary button opens the enquiry form. */
const ENQUIRY_CTA_LABEL = "Have an Enquiry?";

/* Every screen asset is a finished render  phone body, bezel, angle and
   shadow are all baked into the PNG, sitting inside a larger transparent canvas.
   So we describe the canvas → body relationship instead of framing anything:
     k       canvas height / device length, so --ma-body-h sizes the *phone*
     dx      shift (as % of canvas width) that centres the body, not the canvas
     bottom  where the device's lowest point sits, as a fraction of canvas
             height  the podium's top face is anchored to it
   k is measured from each PNG's opaque bounds, then eased up on the tilted
   renders  their bounding box is taller than the device actually reads.

   The rest exist so the podium can be sized and placed against the device
   rather than the canvas it floats in. All read off the same opaque bounds:
     ar      canvas width / canvas height
     w       body width / canvas width  on a tilted render this is the rotated
             bounding box, i.e. the footprint the device actually presents
     fx      centre of the body's *bottom edge*, as an offset from the canvas
             centre in canvas widths, dx folded in. A tilted device touches
             down well off to one side, and that contact point  not the
             bounding box  is what the plinth has to sit under.
     sink    how far to press the device into the top face, in body heights.
             A steeply tilted render meets the plate on one corner, so without
             this its leading edge hovers off the back rim.
     upright square to the camera, so it takes the wider reference platform. */
const SHOT = {
  pulse: { k: 1.37, dx: "-2.05%", bottom: 0.893, ar: 0.6667, w: 0.7881, fx: 0.0415, sink: 0.071 },
  journal: { k: 1.12, dx: "0.2%", bottom: 0.97, ar: 0.6667, w: 0.7246, fx: -0.0688 },
  chatbot: { k: 1.074, dx: "0%", bottom: 0.953, ar: 0.6667, w: 0.6826, fx: 0, upright: true },
  insights: { k: 1.1, dx: "-0.96%", bottom: 0.995, ar: 1, w: 0.5837, fx: -0.0004, sink: 0.069 },
  wellbeing: { k: 1.16, dx: "-3.61%", bottom: 0.94, ar: 1.5, w: 0.3346 },
  counsellor: { k: 1.409, dx: "-0.15%", bottom: 0.85, ar: 0.6667, w: 0.4902 },
  goals: { k: 1.016, dx: "0%", bottom: 0.989, ar: 1, w: 0.4769, fx: 0, upright: true },
};

/* The two environment plates. Each already carries its own plinth, mountains
   and foliage, so the side is a straight asset swap  never a CSS flip. */
const PODIUM = { left: podiumLeft, right: podiumRight };

/* Six features, told one scroll at a time. `side` is where the device sits,
   and which environment plate it lands on. */
const FEATURES = [
  {
    id: "pulse",
    num: "01",
    label: "Daily Check-in",
    titleTop: "Start by noticing",
    titleAccent: "how you feel.",
    solid: true,
    lede: "A quick check-in gives you a moment to notice how you're feeling. Over time, those small moments can help you see patterns you might otherwise miss.",
    img: screenPulse,
    alt: "Sentio Mind home screen with the daily emotion check-in",
    shot: SHOT.pulse,
    side: "left",
    tint: true,
  },
  {
    id: "journal",
    num: "02",
    label: "Journal",
    titleTop: "Write freely.",
    titleAccent: "See what keeps coming back.",
    solid: true,
    lede: "Write the way you think  messy, unfiltered, honest. Sentio helps you explore the emotions and patterns that emerge over time.",
    img: screenJournal,
    alt: "Sentio Mind journal screen with AI emotion analysis",
    shot: SHOT.journal,
    side: "right",
    tint: true,
  },
  {
    id: "companion",
    num: "03",
    label: "AI Companion",
    titleTop: "Your thoughts deserve",
    titleAccent: "a listener.",
    lede: "A space to think out loud, whenever you need it. No judgement, no pressure  just a place to explore what's on your mind.",
    support: "Here at 2 AM. Here on the good days too.",
    img: screenChatbot,
    alt: "Sentio Mind AI companion chat screen",
    shot: SHOT.chatbot,
    side: "left",
    tint: true,
  },
  {
    id: "insights",
    num: "04",
    label: "Personalised Insights",
    titleTop: "See what your emotions",
    titleAccent: "may be telling you.",
    lede: "Check-ins, journal entries and conversations come together to help you see patterns over time  giving you a clearer picture to reflect on.",
    img: screenInsights,
    alt: "Sentio Mind personalised insights screen",
    shot: SHOT.insights,
    side: "right",
  },
];

const GOALS_FEATURE = {
  id: "goals",
  num: "06",
  label: "Smart Goals",
  titleTop: "Growth, measured in",
  titleAccent: "gentle steps.",
  lede: "Choose what you want to work toward, set your own pace, and use Sentio for gentle reminders and encouragement along the way.",
  support: "Progress you can feel, not just tick off.",
  img: screenGoals,
  alt: "Sentio Mind smart goals screen",
  shot: SHOT.goals,
  side: "left",
  tint: true,
};

/* ── Breakpoints that behaviour has to agree with ──────────────────────────
   CSS owns every look on this page; these two queries exist only where JS has
   to make the same decision the stylesheet already made, and they are written
   to match it exactly. Change one of these and you must change the matching
   query in MobileAppStyles.js.

   TABLET_DOWN  the tablet line  where the wellness orbit becomes a row, so
                only one of the two chip sets is real at a time.
   PARALLAX_OK  the hero background only parallaxes on a laptop and up, with a
                fine pointer, with motion allowed. Reduced-motion alone was not
                enough: a scroll-driven transform on a full-bleed background is
                at its most expensive and most visible exactly where it is
                least wanted, and a phone has neither the fill rate for it nor
                the height for the effect to read. */
const TABLET_DOWN = "(max-width: 1068px)";
const PARALLAX_OK =
  "(min-width: 1069px) and (pointer: fine) and (prefers-reduced-motion: no-preference)";

/* One subscription per query. Initialised from matchMedia rather than a
   default, so the first paint is already correct and nothing flashes. */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/* Category chips orbit the wellness device on desktop and wrap into a row below it on smaller screens. */
const EXERCISE_CATEGORIES = [
  { label: "Quick Calm", top: "6%", left: "4%" },
  { label: "Sleep and Wind Down", top: "24%", left: "72%" },
  { label: "Focus and Mental Clarity", top: "44%", left: "0%" },
  { label: "Emotional Balance", top: "62%", left: "74%" },
  { label: "Release Tension", top: "80%", left: "8%" },
  { label: "Recharge", top: "88%", left: "66%" },
];

/* ── Small building blocks ─────────────────────────────────────────────────── */

const AppleIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const PlayIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.6 2.3a1 1 0 0 0-.5.9v17.6a1 1 0 0 0 .5.9l9.2-9.7L3.6 2.3z" />
    <path d="M17.1 8.6 5.3 1.9a1 1 0 0 0-1 .05l9.5 10 3.3-3.35z" />
    <path d="m20.6 10.6-2.7-1.5-3.5 3.4 3.5 3.4 2.7-1.5c.9-.5.9-1.8 0-2.3z" />
    <path d="M4.3 22.05a1 1 0 0 0 1 .05l11.8-6.7-3.3-3.35-9.5 10z" />
  </svg>
);

/*
  Store badges. Until the listings exist these are not links  a badge that
  reads "Download" but goes nowhere implies the app is already out. Each renders
  as a static marker whose label says the app is coming, and only becomes an
  anchor once STORE_LINKS carries a real URL.
*/
function StoreBadge({ href, icon, store, fullName, variant }) {
  const className = `ma-store${variant === "solid" ? " ma-store-light" : ""}${href ? "" : " ma-store-soon"}`;
  const content = (
    <>
      {icon}
      <span className="ma-store-text">
        <span className="ma-store-small">{href ? "Download on" : "Coming soon to"}</span>
        <span className="ma-store-big">{store}</span>
      </span>
    </>
  );

  if (href) {
    return (
      <a className={className} href={href} aria-label={`Download Sentio Mind on ${fullName}`}>
        {content}
      </a>
    );
  }
  return (
    <span className={className} role="img" aria-label={`Sentio Mind is coming soon to ${fullName}`}>
      {content}
    </span>
  );
}

function StoreButtons({ variant = "glass" }) {
  return (
    <>
      <StoreBadge
        href={STORE_LINKS.ios}
        icon={<AppleIcon />}
        store="App Store"
        fullName="the Apple App Store"
        variant={variant}
      />
      <StoreBadge
        href={STORE_LINKS.android}
        icon={<PlayIcon />}
        store="Google Play"
        fullName="the Google Play Store"
        variant={variant}
      />
    </>
  );
}

/**
 * A finished phone render, placed as-is. The PNG already carries the body,
 * frame, angle and shadow, so nothing here frames, tilts or masks it  the
 * wrapper only scales it by the phone body and reuses the existing float.
 *
 * `podium` drops the matching environment plate behind the device. It is a
 * sibling, never a background: the plate is anchored off the same measurements
 * the phone is sized by, so the plinth's top face always meets the device.
 */
function PhoneShot({ img, alt, shot, floatDelay = "0s", className = "", float = true, podium }) {
  /* width/height carry the render's intrinsic aspect ratio, which the CSS
     already knows as --ma-canvas-ar. They are not a size  the stylesheet sets
     height off --ma-body-h and leaves width auto  they only let the browser
     reserve the right box before a 1MB PNG has decoded, so a stacked section
     doesn't reflow underneath itself on a slow connection. */
  const picture = (
    <img
      className="ma-shot"
      src={img}
      alt={alt}
      width={Math.round(1000 * shot.ar)}
      height={1000}
      loading="lazy"
      decoding="async"
    />
  );
  const staging = podium
    ? `ma-staged ma-staged-${podium}${shot.upright ? " ma-staged-upright" : ""}`
    : "";
  return (
    <div
      className={`ma-shot-stage ${staging} ${className}`}
      style={{
        "--ma-canvas-k": shot.k,
        "--ma-canvas-ar": shot.ar,
        "--ma-shot-dx": shot.dx,
        "--ma-shot-w": shot.w,
        "--ma-shot-bottom": shot.bottom,
        "--ma-shot-fx": shot.fx ?? 0,
        "--ma-shot-sink": shot.sink ?? 0,
      }}
      data-reveal
    >
      {podium && (
        <img
          className="ma-podium"
          src={PODIUM[podium]}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
      )}
      {float ? (
        <div className="ma-float" style={{ "--ma-float-delay": floatDelay }}>{picture}</div>
      ) : (
        picture
      )}
    </div>
  );
}

/* A three-stroke twinkle mark for either side of the hero's launch badge.
   One drawing, mirrored inside the SVG for the left side so the element's own
   transform stays free for the twinkle animation. The right cluster's lowest
   stroke fades into the brand teal  the badge's only spot of colour. */
function HeroSparkle({ side }) {
  const right = side === "right";
  return (
    <svg
      className={`ma-flag-spark ma-flag-spark-${right ? "r" : "l"}`}
      viewBox="0 0 34 34"
      width="34"
      height="34"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {right && (
        <defs>
          <linearGradient id="maSparkTeal" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#39b0b3" />
          </linearGradient>
        </defs>
      )}
      {/* The left mark is the same burst flipped about the viewBox centre. */}
      <g
        transform={right ? undefined : "translate(34,0) scale(-1,1)"}
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <line x1="6.5" y1="17" x2="22.5" y2="17" />
        <line x1="7.5" y1="13.8" x2="20" y2="6" />
        <line
          x1="7.5"
          y1="20.2"
          x2="20"
          y2="28"
          stroke={right ? "url(#maSparkTeal)" : "#ffffff"}
        />
      </g>
    </svg>
  );
}

/* `solid` drops the gradient accent and sets the whole heading in ink. */
function SectionHeading({ num, label, titleTop, titleAccent, solid = false }) {
  return (
    <>
      <div className="ma-label">
        <span className="ma-label-num">{num}</span>
        <span className="ma-label-rule" />
        {label}
      </div>
      <h2 className={`ma-h2${solid ? " ma-h2-ink" : ""}`}>
        {titleTop}{" "}
        {solid ? titleAccent : <span className="ma-h2-grad">{titleAccent}</span>}
      </h2>
    </>
  );
}

function FeatureSection({ feature }) {
  const { num, label, titleTop, titleAccent, solid, lede, support, img, alt, side, shot, tint } = feature;
  const floatDelay = `${(Number(num) % 3) * 0.6}s`;
  return (
    <section className={`ma-band ma-feature${tint ? " ma-feature-tint" : ""}`}>
      <div className="ma-band-inner">
        <div className={`ma-feature-grid ${side === "left" ? "ma-media-left" : "ma-media-right"}`}>
          <div className="ma-feature-media">
            <PhoneShot img={img} alt={alt} shot={shot} floatDelay={floatDelay} podium={side} />
          </div>

          <div className="ma-feature-copy ma-reveal" data-reveal>
            <SectionHeading num={num} label={label} titleTop={titleTop} titleAccent={titleAccent} solid={solid} />
            <p className="ma-lede">{lede}</p>
            {support && <p className="ma-support">{support}</p>}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */

export default function MobileAppLanding() {
  const navigate = useNavigate();
  const [introComplete, setIntroComplete] = useState(hasSeenIntro);
  const [showDemo, setShowDemo] = useState(false);
  const heroBgRef = useRef(null);
  const heroRef = useRef(null);
  const stacked = useMediaQuery(TABLET_DOWN);
  const parallax = useMediaQuery(PARALLAX_OK);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Meta Pixel ViewContent — the visitor is looking at the launch page. This
     is the top of the funnel, so it belongs on arrival rather than on a scroll
     threshold.

     trackViewContent is idempotent per page load, so StrictMode's double
     effect in development and any remount of this page both send one event.

     Consent-gated, like everything Meta. onMarketingConsent runs the tracking
     call now if the visitor has already accepted, and otherwise waits for them
     to accept — this page is still on screen while the banner is up, so the
     event describes what they are looking at rather than replaying something
     from before they agreed. Without consent it never runs at all. */
  useEffect(() => onMarketingConsent(trackViewContent), []);

  // Scroll reveal  everything marked data-reveal animates in once.
  useEffect(() => {
    if (showDemo) return;
    const elements = document.querySelectorAll("[data-reveal]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("ma-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -70px 0px" }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [showDemo]);

  // Hero parallax  one rAF-throttled listener writing straight to the node.
  // PARALLAX_OK folds in the reduced-motion check along with the pointer and
  // width gates, so this either runs on a machine that can afford it or does
  // not run at all. When it is off the node is cleared back to its CSS resting
  // transform, which matters on a tablet rotated from landscape to portrait
  // mid-scroll: without the reset it would keep whatever offset it had when
  // the query stopped matching.
  useEffect(() => {
    if (showDemo) return;
    if (!parallax) {
      if (heroBgRef.current) heroBgRef.current.style.transform = "";
      return;
    }

    let frame = null;
    const apply = () => {
      frame = null;
      const node = heroBgRef.current;
      if (!node) return;
      const y = window.scrollY;
      if (y > window.innerHeight * 1.2) return;
      node.style.transform = `translate3d(0, ${y * 0.22}px, 0) scale(1.06)`;
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [showDemo, parallax]);

  useEffect(() => {
    document.body.style.overflow = showDemo ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showDemo]);

  const openDemo = useCallback(() => {
    setIntroComplete(true);
    setShowDemo(true);
  }, []);

  const goToPlatform = useCallback((sectionId) => {
    navigate("/platform", sectionId ? { state: { scrollTo: sectionId } } : undefined);
  }, [navigate]);

  // The floating pill carries the logo and two destinations. Both resolve to
  // this page's own sections  Privacy to the privacy band, Contact to the
  // footer, where the contact details already sit. The logo itself is "home".
  const navLinks = NAV_SECTIONS
    .filter((section) => section.id === "trust" || section.id === "contact")
    .map((section) =>
      section.id === "trust"
        ? { ...section, id: "privacy", label: "Privacy", href: "#ma-privacy" }
        : { ...section, href: "#site-footer" }
    );

  const handleNavNavigate = useCallback((id) => {
    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (id === "privacy") {
      document.getElementById("ma-privacy")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (id === "contact") {
      document.getElementById("site-footer")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    goToPlatform(id);
  }, [goToPlatform]);

  const navigateFromDemo = useCallback((sectionId) => {
    setShowDemo(false);
    if (!sectionId || sectionId === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    goToPlatform(sectionId);
  }, [goToPlatform]);

  return (
    <>
      {showDemo && (
        <div id="demo-page-root">
          <BookDemo
            onNavigate={navigateFromDemo}
            onBack={() => navigateFromDemo("home")}
            backLabel="Back to Sentio Mind"
          />
        </div>
      )}

      <div className="ma-page" style={showDemo ? { display: "none" } : undefined} aria-hidden={showDemo}>
        <style>{mobileStyle}</style>

        {!introComplete && <BrandIntro onDone={() => setIntroComplete(true)} />}

        <SiteNav
          variant="floating"
          heroRef={heroRef}
          links={navLinks}
          onNavigate={handleNavNavigate}
          onOpenDemo={openDemo}
          ctaLabel={ENQUIRY_CTA_LABEL}
        />

        {/* ── HERO ───────────────────────────────────────────────────────────── */}
        <section id="ma-home" className="ma-hero" ref={heroRef}>
          <div
            className="ma-hero-bg"
            ref={heroBgRef}
            style={{ backgroundImage: `url(${heroBackground})` }}
            aria-hidden="true"
          />
          <div className="ma-hero-tint" />
          <div className="ma-hero-fade" />

          <div className="ma-hero-inner">
            <div>
              {/* The launch flag reads as a badge, not a tag: a frosted pill
                  flanked by two twinkling sparkle marks. The outer wrapper owns
                  the entry fade so the inner row is free to keep bobbing. */}
              <div className="ma-hero-flagwrap">
                <div className="ma-hero-flagrow">
                  <HeroSparkle side="left" />
                  <p className="ma-hero-flag">WE ARE LAUNCHING ON 2nd OCTOBER 2026!</p>
                  <HeroSparkle side="right" />
                </div>
              </div>

              <h1>
                Understand yourself.
                <span className="ma-h1-soft">At your own pace.</span>
              </h1>

              <p className="ma-hero-lede">
                Sentio brings AI, psychology and everyday habit science together to help you
                understand how you feel  and decide what feels right for you.
              </p>

              {/* The one thing this page is for. */}
              <div className="ma-hero-cta">
                <button className="ma-btn-primary" onClick={openDemo}>
                  {ENQUIRY_CTA_LABEL}
                </button>
                <button className="ma-btn-glass" onClick={() => goToPlatform()}>Explore our other Platform</button>
              </div>

              <div className="ma-hero-stores">
                <StoreButtons />
              </div>

              <p className="ma-hero-note">
                Private by design <span /> DPDP Act 2023 compliant <span /> Yours alone
              </p>
            </div>

            {/* The two hero PNGs are complete phone renders  frame, angle and
                shadow are in the asset, so they go straight onto the sky.
                width/height are the assets' own pixel dimensions, present only
                to give the browser the aspect ratio up front. */}
            <div className="ma-hero-cluster">
              <div className="ma-hero-halo" />
              <div className="ma-hero-phone ma-hero-phone-back">
                <img
                  src={screenWellbeing}
                  alt="Sentio Mind wellbeing screen"
                  style={{ "--ma-float-delay": "1.2s" }}
                  width={1526}
                  height={1024}
                  decoding="async"
                />
              </div>
              <div className="ma-hero-phone ma-hero-phone-front">
                <img
                  src={screenHome}
                  alt="Sentio Mind home screen"
                  style={{ "--ma-float-delay": "0s" }}
                  width={1024}
                  height={1536}
                  decoding="async"
                  fetchPriority="high"
                />
              </div>
            </div>

            {/* Decorative hand-written note in the sky, to the right of the
                phones. Purely ornamental  aria-hidden and pointer-events:none
                so it never sits between a tap and the CTA beneath it. It used
                to be painted into herobackground.png, where `cover` cropped it
                off on anything narrower than the asset. */}
            <div className="ma-hero-tagline" aria-hidden="true">
              <span>Small</span>
              <span>Steps</span>
              <span className="ma-tagline-accent">Brighter</span>
              <span className="ma-tagline-accent">Days</span>
            </div>
          </div>
        </section>

        {/* ── FEATURES 01–04 ─────────────────────────────────────────────────── */}
        <div style={{ position: "relative" }}>
          <div className="ma-bloom" style={{ width: 620, height: 620, top: "4%", left: "-14%", background: "radial-gradient(circle, rgba(94,196,245,0.20) 0%, transparent 70%)" }} />
          <div className="ma-bloom" style={{ width: 520, height: 520, top: "46%", right: "-12%", background: "radial-gradient(circle, rgba(99,102,241,0.14) 0%, transparent 70%)" }} />

          {FEATURES.map((feature) => (
            <FeatureSection key={feature.id} feature={feature} />
          ))}
        </div>

        {/* ── 05 · PERSONALISED WELLNESS EXERCISES ───────────────────────────── */}
        <section className="ma-band ma-wellness">
          <div className="ma-band-inner">
            <div className="ma-wellness-head ma-reveal" data-reveal>
              <SectionHeading
                num="05"
                label="Personalised Wellness"
                titleTop="Find what feels right for you,"
                titleAccent="right now."
                solid
              />
              <p className="ma-lede">
                Explore practices based on what you're looking for  whether you have two quiet
                minutes or a little more time for yourself.
              </p>
            </div>

            <div className="ma-wellness-stage" data-reveal>
              <div className="ma-wellness-ring" />
              <div className="ma-wellness-ring ma-ring-inner" />

              {/* Both renderings of the same six categories are mounted, and
                  the stylesheet display: none's whichever one the current
                  layout doesn't use. aria-hidden is belt and braces on top of
                  that: display: none does drop a subtree from the
                  accessibility tree, but this way the inactive set is
                  unreachable even if a future rule swaps to visibility or
                  opacity, and the labels can never be announced twice. */}
              {EXERCISE_CATEGORIES.map((category, i) => (
                <span
                  key={category.label}
                  className="ma-chip ma-chip-orbit ma-chip-float"
                  aria-hidden={stacked || undefined}
                  style={{
                    top: category.top,
                    left: category.left,
                    /* Same number as `left`, handed to CSS so a chip on the
                       right-hand arc can cap its width at the room actually
                       left before the stage edge instead of running off it. */
                    "--ma-chip-x": category.left,
                    "--ma-delay": `${0.25 + i * 0.11}s`,
                    "--ma-float-delay": `${i * 0.5}s`,
                  }}
                >
                  <span className="ma-chip-dot" />
                  {category.label}
                </span>
              ))}

              <PhoneShot
                img={screenWellbeing}
                alt="Sentio Mind wellness exercise library"
                shot={SHOT.wellbeing}
                floatDelay="0.3s"
                className="ma-wellness-shot"
              />

              {/* Stacked layouts get the same chips as a wrapped row instead of an orbit */}
              <div className="ma-chip-row" aria-hidden={!stacked || undefined}>
                {EXERCISE_CATEGORIES.map((category, i) => (
                  <span
                    key={category.label}
                    className="ma-chip"
                    style={{ "--ma-delay": `${0.2 + i * 0.08}s` }}
                  >
                    <span className="ma-chip-dot" />
                    {category.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 06 · SMART GOALS ───────────────────────────────────────────────── */}
        <FeatureSection feature={GOALS_FEATURE} />

        {/* ── 07 · INSIDE A CONVERSATION (scroll-linked chapters) ────────────── */}
        <ScrollChapters />

        {/* ── HUMAN SUPPORT ──────────────────────────────────────────────────── */}
        <section className="ma-band ma-human">
          <div className="ma-band-inner">
            <div className="ma-human-grid">
              <div className="ma-reveal" data-reveal>
                <div className="ma-label">
                  <span className="ma-label-rule" />
                  Human Support
                </div>
                <h2 className="ma-h2">
                  And when you would rather{" "}
                  <span className="ma-h2-accent">talk to a person.</span>
                </h2>
                <p className="ma-lede">
                  Verified counsellors, browsable any time. You decide when that moment is.
                </p>
              </div>

              <PhoneShot
                img={screenCounsellor}
                alt="Sentio Mind counsellor directory"
                shot={SHOT.counsellor}
                className="ma-human-shot"
                float={false}
              />
            </div>
          </div>
        </section>

        {/* ── PRIVACY ────────────────────────────────────────────────────────── */}
        <PrivacySection />

        {/* ── CLOSING CTA ────────────────────────────────────────────────────── */}
        <section className="ma-band ma-cta">
          <div className="ma-band-inner ma-reveal" data-reveal>
            <h2 className="ma-h2">
              Not an AI therapist.{" "}
              <span className="ma-h2-grad">Not another wellness app.</span>
            </h2>
            <p className="ma-lede">
              A companion for understanding yourself. One honest check-in is enough to begin.
            </p>

            <div className="ma-cta-actions">
              <button className="ma-btn-primary" onClick={openDemo}>
                {ENQUIRY_CTA_LABEL}
              </button>
              <button className="ma-btn-ghost" onClick={() => goToPlatform()}>Explore our other Platform</button>
            </div>

            <div className="ma-cta-stores">
              <StoreButtons variant="solid" />
            </div>
          </div>
        </section>

        <SiteFooter contactEmail="enquiry@sentiomind.in" />
      </div>
    </>
  );
}
